/**
 * Settings Page
 * Business and user settings management
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Button,
  Input,
} from '@/components/ui/index.js';
import { settingsService } from '@/services/settings.js';
import type { TenantSettings, TaxRate, UserProfile } from '@/types/settings.js';

type SettingsTab = 'business' | 'invoicing' | 'taxes' | 'profile' | 'security';

export function SettingsPage() {
  const [activeTab, setActiveTab] = useState<SettingsTab>('business');
  const [settings, setSettings] = useState<TenantSettings | null>(null);
  const [taxRates, setTaxRates] = useState<TaxRate[]>([]);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const fetchSettings = useCallback(async () => {
    setLoading(true);
    try {
      const [settingsData, taxRatesData, profileData] = await Promise.all([
        settingsService.getSettings(),
        settingsService.getTaxRates(),
        settingsService.getProfile(),
      ]);
      setSettings(settingsData);
      setTaxRates(taxRatesData);
      setProfile(profileData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load settings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const handleSaveSettings = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!settings) return;

    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const formData = new FormData(e.currentTarget);
      const data = Object.fromEntries(formData.entries());
      await settingsService.updateSettings(data);
      setSuccess('Settings saved successfully');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const tabs: { id: SettingsTab; label: string; icon: React.ReactNode }[] = [
    {
      id: 'business',
      label: 'Business',
      icon: (
        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
        </svg>
      ),
    },
    {
      id: 'invoicing',
      label: 'Invoicing',
      icon: (
        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      ),
    },
    {
      id: 'taxes',
      label: 'Tax Rates',
      icon: (
        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
        </svg>
      ),
    },
    {
      id: 'profile',
      label: 'Profile',
      icon: (
        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
        </svg>
      ),
    },
    {
      id: 'security',
      label: 'Security',
      icon: (
        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
        </svg>
      ),
    },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <svg
          className="h-8 w-8 animate-spin text-primary-600"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          />
        </svg>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
        <p className="mt-1 text-sm text-gray-500">
          Manage your business and account settings
        </p>
      </div>

      {/* Messages */}
      {error && (
        <div className="mb-6 rounded-lg bg-red-50 p-4 text-sm text-red-700">
          {error}
          <button
            onClick={() => setError(null)}
            className="ml-2 font-medium underline hover:text-red-800"
          >
            Dismiss
          </button>
        </div>
      )}

      {success && (
        <div className="mb-6 rounded-lg bg-green-50 p-4 text-sm text-green-700">
          {success}
        </div>
      )}

      <div className="flex flex-col lg:flex-row gap-8">
        {/* Tabs */}
        <nav className="flex lg:flex-col gap-1 lg:w-48">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-3 rounded-lg px-4 py-3 text-sm font-medium transition-colors ${
                activeTab === tab.id
                  ? 'bg-primary-50 text-primary-700'
                  : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
              }`}
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </nav>

        {/* Content */}
        <div className="flex-1">
          {/* Business Settings */}
          {activeTab === 'business' && settings && (
            <Card>
              <CardHeader>
                <CardTitle>Business Information</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSaveSettings} className="space-y-6">
                  <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                    <Input
                      label="Business Name"
                      name="name"
                      defaultValue={settings.name}
                      required
                    />
                    <Input
                      label="Email"
                      name="email"
                      type="email"
                      defaultValue={settings.email || ''}
                    />
                    <Input
                      label="Phone"
                      name="phone"
                      type="tel"
                      defaultValue={settings.phone || ''}
                    />
                    <Input
                      label="Website"
                      name="website"
                      type="url"
                      defaultValue={settings.website || ''}
                    />
                    <Input
                      label="Tax ID"
                      name="taxId"
                      defaultValue={settings.taxId || ''}
                    />
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Currency
                      </label>
                      <select
                        name="currency"
                        defaultValue={settings.currency}
                        className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                      >
                        <option value="USD">USD - US Dollar</option>
                        <option value="EUR">EUR - Euro</option>
                        <option value="GBP">GBP - British Pound</option>
                        <option value="CAD">CAD - Canadian Dollar</option>
                        <option value="AUD">AUD - Australian Dollar</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Timezone
                      </label>
                      <select
                        name="timezone"
                        defaultValue={settings.timezone}
                        className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                      >
                        <option value="America/New_York">Eastern Time</option>
                        <option value="America/Chicago">Central Time</option>
                        <option value="America/Denver">Mountain Time</option>
                        <option value="America/Los_Angeles">Pacific Time</option>
                        <option value="Europe/London">London</option>
                        <option value="Europe/Paris">Paris</option>
                        <option value="Asia/Tokyo">Tokyo</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Address
                    </label>
                    <div className="mt-1 grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <Input
                        name="address.street"
                        placeholder="Street"
                        defaultValue={settings.address?.street || ''}
                      />
                      <Input
                        name="address.city"
                        placeholder="City"
                        defaultValue={settings.address?.city || ''}
                      />
                      <Input
                        name="address.state"
                        placeholder="State/Province"
                        defaultValue={settings.address?.state || ''}
                      />
                      <Input
                        name="address.postalCode"
                        placeholder="Postal Code"
                        defaultValue={settings.address?.postalCode || ''}
                      />
                      <Input
                        name="address.country"
                        placeholder="Country"
                        defaultValue={settings.address?.country || ''}
                      />
                    </div>
                  </div>

                  <div className="flex justify-end">
                    <Button type="submit" disabled={saving}>
                      {saving ? 'Saving...' : 'Save Changes'}
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          )}

          {/* Invoicing Settings */}
          {activeTab === 'invoicing' && settings && (
            <Card>
              <CardHeader>
                <CardTitle>Invoice Settings</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSaveSettings} className="space-y-6">
                  <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                    <Input
                      label="Invoice Prefix"
                      name="invoicePrefix"
                      defaultValue={settings.invoicePrefix}
                      placeholder="INV-"
                    />
                    <Input
                      label="Next Invoice Number"
                      name="invoiceNextNumber"
                      type="number"
                      defaultValue={settings.invoiceNextNumber}
                    />
                    <Input
                      label="Default Payment Terms (days)"
                      name="paymentTerms"
                      type="number"
                      defaultValue={settings.paymentTerms}
                    />
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Fiscal Year Start
                      </label>
                      <select
                        name="fiscalYearStart"
                        defaultValue={settings.fiscalYearStart}
                        className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                      >
                        <option value={1}>January</option>
                        <option value={4}>April</option>
                        <option value={7}>July</option>
                        <option value={10}>October</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Default Invoice Terms
                    </label>
                    <textarea
                      name="invoiceTerms"
                      rows={3}
                      defaultValue={settings.invoiceTerms || ''}
                      className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                      placeholder="Terms and conditions..."
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Default Invoice Footer
                    </label>
                    <textarea
                      name="invoiceFooter"
                      rows={2}
                      defaultValue={settings.invoiceFooter || ''}
                      className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                      placeholder="Thank you for your business!"
                    />
                  </div>

                  <div className="flex justify-end">
                    <Button type="submit" disabled={saving}>
                      {saving ? 'Saving...' : 'Save Changes'}
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          )}

          {/* Tax Rates */}
          {activeTab === 'taxes' && (
            <Card>
              <CardHeader className="flex items-center justify-between">
                <CardTitle>Tax Rates</CardTitle>
                <Button variant="outline" size="sm">
                  Add Tax Rate
                </Button>
              </CardHeader>
              <CardContent>
                {taxRates.length === 0 ? (
                  <p className="text-center text-sm text-gray-500 py-8">
                    No tax rates configured. Add your first tax rate.
                  </p>
                ) : (
                  <div className="divide-y divide-gray-200">
                    {taxRates.map((rate) => (
                      <div
                        key={rate.id}
                        className="flex items-center justify-between py-4"
                      >
                        <div>
                          <p className="font-medium text-gray-900">{rate.name}</p>
                          <p className="text-sm text-gray-500">{rate.rate}%</p>
                        </div>
                        <div className="flex items-center gap-4">
                          {rate.isDefault && (
                            <span className="text-xs text-primary-600 font-medium">
                              Default
                            </span>
                          )}
                          <button className="text-gray-400 hover:text-gray-600">
                            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Profile Settings */}
          {activeTab === 'profile' && profile && (
            <Card>
              <CardHeader>
                <CardTitle>Profile Information</CardTitle>
              </CardHeader>
              <CardContent>
                <form className="space-y-6">
                  <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                    <Input
                      label="First Name"
                      name="firstName"
                      defaultValue={profile.firstName}
                      required
                    />
                    <Input
                      label="Last Name"
                      name="lastName"
                      defaultValue={profile.lastName}
                      required
                    />
                    <Input
                      label="Email"
                      name="email"
                      type="email"
                      defaultValue={profile.email}
                      disabled
                    />
                    <Input
                      label="Phone"
                      name="phone"
                      type="tel"
                      defaultValue={profile.phone || ''}
                    />
                  </div>

                  <div className="flex justify-end">
                    <Button type="submit" disabled={saving}>
                      {saving ? 'Saving...' : 'Save Changes'}
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          )}

          {/* Security Settings */}
          {activeTab === 'security' && profile && (
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>Change Password</CardTitle>
                </CardHeader>
                <CardContent>
                  <form className="space-y-6">
                    <Input
                      label="Current Password"
                      name="currentPassword"
                      type="password"
                      required
                    />
                    <Input
                      label="New Password"
                      name="newPassword"
                      type="password"
                      required
                    />
                    <Input
                      label="Confirm New Password"
                      name="confirmPassword"
                      type="password"
                      required
                    />
                    <div className="flex justify-end">
                      <Button type="submit">Update Password</Button>
                    </div>
                  </form>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Two-Factor Authentication</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-gray-900">
                        {profile.mfaEnabled ? 'Enabled' : 'Disabled'}
                      </p>
                      <p className="text-sm text-gray-500">
                        Add an extra layer of security to your account
                      </p>
                    </div>
                    <Button variant={profile.mfaEnabled ? 'destructive' : 'primary'}>
                      {profile.mfaEnabled ? 'Disable' : 'Enable'} MFA
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default SettingsPage;
